import { useTranslation } from 'react-i18next';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router';

import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Page } from '@/shared/ui/Page';
import { Skeleton, SkeletonText } from '@/shared/ui/Skeleton';

import type { RouteEntry } from '@/app/route-manifest';

/**
 * L'écran de lancement, celui du mobile : l'emblème BUL immobile, et son
 * anneau d'or qui tourne, le temps de relire la session sur l'appareil.
 *
 * Deux calques tirés de l'image de démarrage (800 × 1000), affichés à 200 de
 * large : l'anneau est un carré centré sur son propre centre, c'est ce qui le
 * fait tourner sur place. Il ne dépend de rien de ce qui se charge : ni
 * police, ni traduction, ni session.
 *
 * Le mobile le garde au moins 1,2 s, pour relayer l'image fixe du système ;
 * le web n'a pas d'image à relayer, il n'ajoute donc aucune attente.
 */
export function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg" aria-busy="true">
      <div role="img" aria-label="BUL" className="relative h-[250px] w-[200px]">
        {/* L'anneau d'abord, le bouclier par-dessus : les coins et la pointe du
            bouclier passent devant l'anneau, comme sur le logo. */}
        <img
          src="/brand/launch-ring.webp"
          alt=""
          draggable={false}
          className="absolute top-[30.9px] left-[28.28px] size-[144px] animate-launch-ring select-none"
        />
        {/* En sombre, le mot « BUL », bleu nuit, se perdrait dans le fond :
            l'emblème reste seul. */}
        <img
          src="/brand/launch-base.webp"
          alt=""
          draggable={false}
          className="absolute inset-0 size-full select-none dark:[clip-path:inset(0_0_28%_0)]"
        />
      </div>
    </div>
  );
}

/** Silhouette d'une page qui arrive : jamais un tourniquet seul au milieu du vide. */
export function PageSkeleton() {
  return (
    <div className="mx-auto w-full max-w-[720px] px-4 pt-6 md:px-6 md:pt-10" aria-busy="true">
      <Skeleton className="h-7 w-1/3" />
      <SkeletonText lines={4} className="mt-6" />
      <Skeleton className="mt-6 h-48 w-full rounded-md" />
    </div>
  );
}

/** Une route dont l'écran n'est pas encore sur le disque : le routeur ne casse jamais. */
export function PendingPage({ entry }: { entry: RouteEntry }) {
  return (
    <Page title={entry.page.replace(/Page$/, '')} back={entry.layout === 'stack'}>
      <EmptyState
        icon="clock"
        title="Écran en construction"
        message={`Équivalent web de l’écran mobile « ${entry.mobile.join(', ') || entry.path} ».`}
      />
    </Page>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation(['common', 'web', 'account']);
  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-4">
      <EmptyState
        icon="compass"
        title={t('web:notFound.title')}
        message={t('web:notFound.message')}
        action={
          <Button asChild variant="primary" size="sm">
            <Link to="/">{t('web:notFound.backHome')}</Link>
          </Button>
        }
      />
    </div>
  );
}

/** Ce qui s'affiche quand une page plante : une phrase, et deux portes de sortie. */
export function CrashPage() {
  const { t } = useTranslation(['common', 'web', 'account']);
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }
  if (import.meta.env.DEV) {
    console.error(error);
  }
  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-4">
      <EmptyState
        icon="alert"
        title={t('web:crash.title')}
        message={t('web:crash.message')}
        action={
          <div className="flex flex-wrap justify-center gap-2.5">
            <Button variant="primary" size="sm" onClick={() => window.location.reload()}>
              {t('web:crash.reload')}
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href="/">{t('web:notFound.backHome')}</a>
            </Button>
          </div>
        }
      />
    </div>
  );
}
